/**
 * Auth Service Hooks
 *
 * React Query hooks for authentication operations
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authService, userService } from '../services/api';
import { AUTH_KEYS } from './keys';
import type {
  ActivateProfileRequest,
  LoginRequest,
  LoginResponse,
  PinVerifyRequest,
  PinVerifyResponse,
  PinSetRequest,
  PinResetRequest,
  PinResetConfirmRequest,
  PinChangeRequest,
  RefreshTokenRequest,
  RefreshTokenResponse,
  UserMeResponse,
  UserInfoResponse,
  SessionsResponse,
  LogoutRequest,
  LogoutResponse,
  AuthUser,
} from '../services/api';

// ==================== MUTATIONS ====================

/**
 * Hook para fazer login
 */
export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [AUTH_KEYS.LOGIN],
    mutationFn: ({ login, senha, channel }: LoginRequest) => authService.login(login, senha, channel),
    onSuccess: (data: LoginResponse) => {
      console.log('Login bem-sucedido, invalidando todas as queries...');
      // Invalidate ALL queries since we have new tokens
      queryClient.clear();
      // Reload the page to restart the app with new authentication state
      window.location.reload();
    },
    onError: (error: Error) => {
      console.error('Erro no login:', error);
    },
  });
}

/**
 * Hook para renovar token
 */
export function useRefreshToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [AUTH_KEYS.REFRESH_TOKEN],
    mutationFn: (data: RefreshTokenRequest) => authService.refreshToken(data.refresh_token),
    onSuccess: () => {
      console.log('Token renovado com sucesso, invalidando todas as queries...');
      // Clear ALL queries since we have new tokens
      queryClient.clear();
      // Force refetch of all currently mounted and enabled queries
      queryClient.refetchQueries({
        type: 'active',
        stale: true,
      });
      // Also specifically invalidate auth-related queries to ensure they refetch immediately
      queryClient.invalidateQueries({ queryKey: [AUTH_KEYS.USER_ME] });
      queryClient.invalidateQueries({ queryKey: [AUTH_KEYS.USER_INFO] });
      queryClient.invalidateQueries({ queryKey: [AUTH_KEYS.CURRENT_USER] });
    },
    onError: (error: Error) => {
      console.error('Erro ao renovar token:', error);
      // Clear auth data on refresh error
      authService.clearLocalAuth();
      queryClient.clear();
    },
  });
}

/**
 * Hook para fazer logout
 */
export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [AUTH_KEYS.LOGOUT],
    mutationFn: (data: LogoutRequest) => authService.logout(data.session_id),
    onSuccess: () => {
      // Clear all queries and auth data
      queryClient.clear();
      authService.clearLocalAuth();
      // Clear establishment selection to prevent cross-user data leakage
      authService.clearEstablishmentSelection();
    },
    onError: (error: Error) => {
      console.error('Erro no logout:', error);
      // Clear auth data even on error
      queryClient.clear();
      authService.clearLocalAuth();
      // Clear establishment selection even on error
      authService.clearEstablishmentSelection();
    },
  });
}

/**
 * Hook para fazer logout de todas as sessões
 */
export function useLogoutAll() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [AUTH_KEYS.LOGOUT_ALL],
    mutationFn: () => authService.logoutAll(),
    onSuccess: () => {
      // Clear all queries and auth data
      queryClient.clear();
      authService.clearLocalAuth();
      // Clear establishment selection to prevent cross-user data leakage
      authService.clearEstablishmentSelection();
    },
    onError: (error: Error) => {
      console.error('Erro no logout de todas as sessões:', error);
      // Clear auth data even on error
      queryClient.clear();
      authService.clearLocalAuth();
      // Clear establishment selection even on error
      authService.clearEstablishmentSelection();
    },
  });
}

/**
 * Hook para selecionar estabelecimento
 */
export function useSelectEstablishment(redirectTo?: string) {
  return useMutation({
    mutationKey: [AUTH_KEYS.SELECT_ESTABLISHMENT],
    mutationFn: async (establishmentId: string) => {
      await authService.postSelectEstablishment(establishmentId, redirectTo);
    },
  });
}

/**
 * Hook para selecionar perfil
 */
export function useSelectProfile() {
  return useMutation({
    mutationKey: [AUTH_KEYS.SELECT_PROFILE],
    mutationFn: async (profileId: string) => {
      await authService.postSelectProfile(profileId);
    },
  });
}

/**
 * Hook para ativar perfil de usuário
 */
export function useActivateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['activateProfile'],
    mutationFn: async (data: ActivateProfileRequest) => {
      try {
        console.log('Iniciando ativação de perfil:', data);

        // Call the user service to activate profile
        const response = await userService.activateProfile(data);
        console.log('Resposta da ativação de perfil:', response);

        // Validate response structure
        if (!response || typeof response !== 'object') {
          console.error('Resposta nula ou não é objeto:', response);
          throw new Error('Resposta inválida do servidor ao ativar perfil');
        }

        if (!response.access_token || !response.session_id) {
          console.error('Resposta sem access_token ou session_id:', response);
          throw new Error('Resposta do servidor não contém tokens necessários');
        }

        // Just store the new tokens - don't call getUserInfo here since queries will be invalidated
        console.log('Armazenando novos tokens após ativação de perfil...');
        const currentTokens = authService.getStoredTokens();
        if (!currentTokens) {
          throw new Error('Nenhum token atual encontrado para preservar refresh_token');
        }

        const newTokenData = {
          access_token: response.access_token,
          refresh_token: currentTokens.refresh_token, // Preserve existing refresh token
          session_id: response.session_id,
          user_id: currentTokens.user_id, // Preserve existing user_id
        };

        // Store updated tokens using apiClient directly
        const { apiClient } = await import('../services/api/apiClient');
        apiClient.storeTokens(newTokenData);
        console.log('✅ Novos tokens armazenados com sucesso');

        return response;
      } catch (error) {
        console.error('Erro na função de ativação de perfil:', error);
        console.error('Stack trace:', error);
        throw error;
      }
    },
    onSuccess: (response) => {
      console.log('🎉 Ativação de perfil bem-sucedida, invalidando cache...', response);

      // Clear ALL queries since we have new tokens
      queryClient.clear();

      // Force refetch of all currently mounted and enabled queries
      // This ensures that active components with data will refetch even if their enabled conditions were met
      queryClient.refetchQueries({
        type: 'active',
        stale: true,
      });

      // Also specifically invalidate auth-related queries to ensure they refetch immediately
      queryClient.invalidateQueries({ queryKey: [AUTH_KEYS.USER_INFO] });
      queryClient.invalidateQueries({ queryKey: [AUTH_KEYS.CURRENT_USER] });
      queryClient.invalidateQueries({ queryKey: [AUTH_KEYS.USER_ME] });

      console.log('✅ Cache invalidado e refetch forçado para queries ativas');
    },
    onError: (error: Error) => {
      console.error('❌ Erro ao ativar perfil:', error);
    },
  });
}

/**
 * Hook para verificar PIN financeiro e obter confirmation_token (step-up auth)
 */
export function usePostPinVerify(options?: any) {
  return useMutation<PinVerifyResponse, Error, PinVerifyRequest>({
    mutationFn: (body: PinVerifyRequest) => authService.postPinVerify(body),
    ...options,
  });
}

/**
 * Hook para definir PIN financeiro usando a senha do usuário
 */
export function usePostPinSet(options?: any) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, PinSetRequest>({
    mutationFn: (body: PinSetRequest) => authService.postPinSet(body),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [AUTH_KEYS.USER_INFO] });
      options?.onSuccess?.(...args);
    },
  });
}

/**
 * Hook para solicitar redefinição de PIN por e-mail
 */
export function usePostPinResetRequest(options?: any) {
  return useMutation<void, Error, PinResetRequest>({
    mutationFn: (body: PinResetRequest) => authService.postPinResetRequest(body),
    ...options,
  });
}

/**
 * Hook para confirmar redefinição de PIN
 */
export function usePostPinResetConfirm(options?: any) {
  return useMutation<void, Error, PinResetConfirmRequest>({
    mutationFn: (body: PinResetConfirmRequest) => authService.postPinResetConfirm(body),
    ...options,
  });
}

/**
 * Hook para alterar PIN financeiro usando o PIN atual
 */
export function usePostPinChange(options?: any) {
  return useMutation<void, Error, PinChangeRequest>({
    mutationFn: (body: PinChangeRequest) => authService.postPinChange(body),
    ...options,
  });
}

// ==================== QUERIES ====================

/**
 * Hook para obter informações completas do usuário
 */
export function useUserInfo() {
  return useQuery({
    queryKey: [AUTH_KEYS.USER_INFO],
    queryFn: () => authService.getUserInfo(),
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: authService.isAuthenticated(),
  });
}

/**
 * Hook para obter sessões do usuário
 */
export function useSessions() {
  return useQuery({
    queryKey: [AUTH_KEYS.SESSIONS],
    queryFn: () => authService.getSessions(),
    retry: false,
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: authService.isAuthenticated(),
  });
}

/**
 * Hook para obter usuário atual
 */
export function useCurrentUser() {
  return useQuery({
    queryKey: [AUTH_KEYS.CURRENT_USER],
    queryFn: () => authService.getCurrentUser(),
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: authService.isAuthenticated(),
  });
}

// ==================== UTILITY HOOKS ====================

/**
 * Hook para verificar se o usuário está autenticado
 */
export function useIsAuthenticated() {
  return useQuery({
    queryKey: [AUTH_KEYS.CURRENT_USER],
    queryFn: () => authService.getCurrentUser(),
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    select: (user: AuthUser | null) => !!user,
    enabled: authService.isAuthenticated(),
  });
}
